import { Fragment, useEffect, useId, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react';
import CloseIcon from '@/assets/icons/close-x.svg?react';
import { useClickOutside } from '@/hooks/useClickOutside';
import styles from './SearchableMultiSelectDropdown.module.scss';

const COMPACT_SELECTION_LIMIT = 2;

export interface MultiSelectOption {
  value: string;
  label: string;
  group?: string;
}

export interface SearchableMultiSelectDropdownProps {
  label?: string;
  options: MultiSelectOption[];
  value: MultiSelectOption[];
  // eslint-disable-next-line no-unused-vars
  onChange: (value: MultiSelectOption[]) => void;
  className?: string;
  disabled?: boolean;
  searchLabel?: string;
  dropdownWidth?: string;
}

function SearchableMultiSelectDropdown({
  label,
  options,
  value,
  onChange,
  className = '',
  disabled = false,
  searchLabel = 'Search clubs or models',
  dropdownWidth,
}: SearchableMultiSelectDropdownProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const optionListId = useId();
  const showExpandedSelections = isExpanded && value.length > COMPACT_SELECTION_LIMIT;
  const visibleOptions = showExpandedSelections ? value : value.slice(0, COMPACT_SELECTION_LIMIT);
  const hiddenOptionCount = value.length - visibleOptions.length;
  const selectedValues = new Set(value.map((option) => option.value));
  const availableOptions = options.filter((option) => !selectedValues.has(option.value) && option.label.toLowerCase().includes(search.trim().toLowerCase()));
  const optionLabel = label ? `${label} options` : 'options';
  const toggleLabel = isOpen ? `Close ${optionLabel}` : `Open ${optionLabel}`;
  const arrowClassName = isOpen ? `${styles.arrow} ${styles.arrowOpen}` : styles.arrow;
  const pillsClassName = showExpandedSelections ? `${styles.pills} ${styles.pillsExpanded}` : styles.pills;
  const wrapperClassName = disabled ? `${styles.wrapper} ${styles.disabled} ${className}` : `${styles.wrapper} ${className}`;

  useClickOutside(wrapperRef, isOpen && !disabled ? 1 : null, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (value.length <= COMPACT_SELECTION_LIMIT) setIsExpanded(false);
  }, [value.length]);

  const handleSelect = (option: MultiSelectOption) => {
    if (disabled) return;

    const isSelected = value.some((selectedOption) => selectedOption.value === option.value);
    if (isSelected) return;

    onChange([...value, option]);
  };

  const handleRemove = (event: MouseEvent<HTMLButtonElement>, optionValue: string) => {
    event.stopPropagation();
    if (disabled) return;

    onChange(value.filter((option) => option.value !== optionValue));
  };

  const handleExpansion = (event: MouseEvent<HTMLButtonElement>, expanded: boolean) => {
    event.stopPropagation();
    if (disabled) return;

    setIsExpanded(expanded);
  };

  const handleToggle = () => {
    if (!disabled) setIsOpen((current) => !current);
  };

  return (
    <div ref={wrapperRef} className={wrapperClassName} aria-disabled={disabled}>
      <fieldset className={styles.field}>
        {label && <legend className={styles.label}>{label}</legend>}
        <div className={styles.control} onClick={handleToggle}>
          <div className={pillsClassName}>
            {visibleOptions.map((option) => (
              <span key={option.value} className={styles.pill}>
                <span className={styles.pillLabel} title={option.label}>
                  {option.label}
                </span>
                <button type="button" className={styles.removeButton} aria-label={`Remove ${option.label}`} disabled={disabled} onClick={(event) => handleRemove(event, option.value)}>
                  <CloseIcon />
                </button>
              </span>
            ))}
            {hiddenOptionCount > 0 && (
              <button
                type="button"
                className={`${styles.pill} ${styles.summaryPill}`}
                aria-label={`Show ${hiddenOptionCount} more selections`}
                disabled={disabled}
                onClick={(event) => handleExpansion(event, true)}
              >
                +{hiddenOptionCount}
              </button>
            )}
            {showExpandedSelections && (
              <button type="button" className={`${styles.pill} ${styles.showLessButton}`} disabled={disabled} onClick={(event) => handleExpansion(event, false)}>
                Show less
              </button>
            )}
          </div>
          <button type="button" className={styles.toggleButton} aria-label={toggleLabel} aria-expanded={isOpen} aria-controls={optionListId} disabled={disabled}>
            <ArrowDownIcon className={arrowClassName} />
          </button>
        </div>
      </fieldset>
      {isOpen && !disabled && (
        <div className={styles.dropdown} style={{ width: dropdownWidth }}>
          <input autoFocus aria-label={searchLabel} placeholder={`${searchLabel}...`} value={search} onChange={(event) => setSearch(event.target.value)} />
          <ul id={optionListId} className={styles.options} role="listbox" aria-label={optionLabel}>
            {availableOptions.map((option, index) => (
              <Fragment key={option.value}>
                {option.group && option.group !== availableOptions[index - 1]?.group && (
                  <li role="presentation" className={styles.sectionTitle}>
                    {option.group}
                  </li>
                )}
                <li role="option" aria-selected="false">
                  <button type="button" onClick={() => handleSelect(option)}>
                    {option.label}
                  </button>
                </li>
              </Fragment>
            ))}
            {availableOptions.length === 0 && (
              <li className={styles.noOptions} role="option" aria-selected="false" aria-disabled="true">
                No matching options available
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchableMultiSelectDropdown;
